/*
 * lazy-sections.js
 * Dociąga treść sekcji leżących poniżej folda, gdy zbliżają się do viewportu.
 * Cel: initial HTML bez ~255 KB martwego markupu (lepszy FCP/LCP; Lighthouse nie
 * scrolluje, więc w teście PSI te sekcje nie pobierają się w ogóle — zero TBT).
 *
 * Obsługiwane cele:
 *  1. Panele tabów: <div class="hdt-tab-item" tab-item data-lazy-collection="<handle>"
 *     data-lazy-fallback="Zobacz wszystko">
 *     Źródło: GET {root}collections/<handle>?section_id=tab-panel-lazy
 *     (sections/tab-panel-lazy.liquid). Panele są display:none, więc IntersectionObserver
 *     nigdy by ich nie zobaczył — obserwujemy ich sekcję-rodzica.
 *  2. Slidery sekcji: <div data-lazy-section="<section.id>">
 *     Źródło: GET <current-path>?section_id=<section.id> — Section Rendering API renderuje
 *     sekcję z JSON-owego szablonu z JEJ ustawieniami (także ręczną listą produktów).
 *     Rekurencji zapobiega guard w Liquidzie: section.index jest nil przy renderze przez
 *     SRA (i w Theme Editorze), więc fetch dostaje pełną treść zamiast placeholdera.
 *
 * Wstrzykiwanie: hdt-reveal/timeline zdejmowane (scroll-reveal skanuje DOM na starcie,
 * późno wstrzyknięte elementy zostałyby niewidoczne). Obrazy eager przemapowywane na lazy —
 * treść fetchowana w kontekście kolekcji dostaje z card-product-media.liquid loading=eager
 * + fetchpriority=high dla pierwszych kart, co w niewidocznej sekcji pobierałoby obrazy
 * na darmo. Custom elementy (hdt-slider defer-init) inicjalizują się same po wejściu do DOM.
 */
(function () {
  'use strict';

  // Guard pojedynczej rejestracji — skrypt emitują wszystkie lazy sekcje na stronie.
  if (window.__hdtLazySections) return;
  window.__hdtLazySections = 1;

  var TAB_PANEL_SECTION = 'tab-panel-lazy';
  var ROOT_MARGIN = '800px 0px'; // zapas na szybki scroll: dociągamy ~1 ekran wcześniej
  var cache = Object.create(null); // url -> Promise<string>

  function rootPath() {
    return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  }

  function fetchHtml(url) {
    if (!cache[url]) {
      cache[url] = fetch(url)
        .then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.text();
        })
        .catch(function (err) {
          delete cache[url]; // ponowna próba (klik taba / kolejny intersect) ma szansę
          throw err;
        });
    }
    return cache[url];
  }

  function sanitize(root) {
    root.querySelectorAll('[hdt-reveal]').forEach(function (el) {
      el.removeAttribute('hdt-reveal');
      el.removeAttribute('timeline');
    });
    root.querySelectorAll('img[loading="eager"]').forEach(function (img) {
      img.setAttribute('loading', 'lazy');
      img.removeAttribute('fetchpriority');
      var s = img.getAttribute('sizes');
      if (s && s.indexOf('auto') !== 0) img.setAttribute('sizes', 'auto, ' + s);
    });
    return root;
  }

  function parse(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  // --- 1. Panele tabów -------------------------------------------------------

  function panelUrl(handle) {
    return rootPath() + 'collections/' + handle + '?section_id=' + TAB_PANEL_SECTION;
  }

  function injectPanel(panel, html) {
    var doc = parse(html);
    var wrap = sanitize(doc.querySelector('.shopify-section') || doc.body);
    panel.innerHTML = wrap.innerHTML;
    panel.removeAttribute('data-lazy-collection');
  }

  function showPanelFallback(panel, handle) {
    var link = document.createElement('a');
    link.className = 'hdt-btn hdt-btn-link';
    link.href = rootPath() + 'collections/' + handle;
    link.textContent = panel.getAttribute('data-lazy-fallback') || 'Zobacz kolekcję';
    panel.innerHTML = '';
    panel.appendChild(link);
    panel.removeAttribute('data-lazy-collection');
  }

  function loadPanel(panel) {
    var handle = panel.getAttribute('data-lazy-collection');
    if (!handle) return;
    fetchHtml(panelUrl(handle)).then(function (html) {
      if (panel.hasAttribute('data-lazy-collection')) injectPanel(panel, html);
    }).catch(function () {
      // Cichy błąd, dopóki panel jest niewidoczny — klik taba ponowi fetch.
      if (panel.hasAttribute('tab-selected')) showPanelFallback(panel, handle);
    });
  }

  // --- 2. Slidery sekcji -----------------------------------------------------

  function sectionUrl(sectionId) {
    return window.location.pathname + '?section_id=' + encodeURIComponent(sectionId);
  }

  function injectSection(ph, html) {
    var doc = parse(html);
    var slider = doc.querySelector('hdt-slider');
    if (!slider) throw new Error('brak hdt-slider w odpowiedzi');
    var frag = document.createDocumentFragment();
    frag.appendChild(sanitize(slider));
    var map = doc.querySelector('script[data-ga4-map]');
    if (map) frag.appendChild(map);
    ph.replaceWith(frag); // placeholder znika razem ze swoim min-height
  }

  function loadSection(ph) {
    var id = ph.getAttribute('data-lazy-section');
    if (!id || ph.hasAttribute('data-lazy-done')) return;
    ph.setAttribute('data-lazy-done', '');
    fetchHtml(sectionUrl(id)).then(function (html) {
      if (ph.isConnected) injectSection(ph, html);
    }).catch(function () {
      ph.removeAttribute('data-lazy-done'); // kolejny intersect spróbuje ponownie
    });
  }

  // --- Trigger ---------------------------------------------------------------

  function whenNear(el, cb) {
    if (!('IntersectionObserver' in window)) { cb(); return; }
    var io = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) { io.disconnect(); cb(); return; }
      }
    }, { rootMargin: ROOT_MARGIN });
    io.observe(el);
  }

  function init() {
    var panels = [].slice.call(document.querySelectorAll('.hdt-tab-item[data-lazy-collection]'));
    var placeholders = [].slice.call(document.querySelectorAll('[data-lazy-section]'));
    if (!panels.length && !placeholders.length) return;

    if (panels.length) {
      // Panele są ukryte (display:none) — obserwujemy ich sekcję, nie je same.
      var sections = [];
      panels.forEach(function (p) {
        var s = p.closest('.shopify-section') || p.parentElement;
        if (s && sections.indexOf(s) === -1) sections.push(s);
      });
      sections.forEach(function (s) {
        whenNear(s, function () {
          s.querySelectorAll('.hdt-tab-item[data-lazy-collection]').forEach(loadPanel);
        });
      });

      // Klik w nagłówek taba, zanim obserwator zdążył dociągnąć → fetch natychmiast.
      document.addEventListener('click', function (e) {
        var t = e.target && e.target.closest ? e.target.closest('hdt-tab button, hdt-tab a') : null;
        if (!t) return;
        document.querySelectorAll('.hdt-tab-item[data-lazy-collection]').forEach(loadPanel);
      }, true);
    }

    placeholders.forEach(function (ph) {
      whenNear(ph, function () { loadSection(ph); });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
